import api from '../../services/api';

// AKM POC §9.3 — rol ataması / pozisyon rolü veri kapsamı. Backend sözleşmesi: Models/DataScopeType.cs
// (0 Varsayılan, 1 Tüm Şirket, 2 Departmanlar, 3 Lokasyonlar, 4 Alt Şirketler) + DataScopeTargetIds.
export const DATA_SCOPE_TYPES = { DEFAULT: 0, COMPANY: 1, DEPARTMENTS: 2, LOCATIONS: 3, SUB_TENANTS: 4 };

const TARGET_FIELD = {
  [DATA_SCOPE_TYPES.DEPARTMENTS]: 'departmentIds',
  [DATA_SCOPE_TYPES.LOCATIONS]: 'locationIds',
  [DATA_SCOPE_TYPES.SUB_TENANTS]: 'subTenantIds',
};

const TYPE_KEY = {
  [DATA_SCOPE_TYPES.DEFAULT]: 'default',
  [DATA_SCOPE_TYPES.COMPANY]: 'company',
  [DATA_SCOPE_TYPES.DEPARTMENTS]: 'departments',
  [DATA_SCOPE_TYPES.LOCATIONS]: 'locations',
  [DATA_SCOPE_TYPES.SUB_TENANTS]: 'subTenants',
};

const listItems = (res) => {
  const r = res?.data?.data;
  return Array.isArray(r) ? r : (r?.data || []);
};

// Hedef seçici: ada göre arama (name[like]); düzenlemede bilinen adlar ağa çıkmadan çözülür.
const targetAsync = (url, known) => ({
  onSearch: async (q) => {
    const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get(url, { params });
    return listItems(res).map((x) => ({ id: x.id, name: x.name }));
  },
  onGetById: async (id) => {
    const hit = known.find((k) => k.id === id);
    if (hit) return { id: hit.id, name: hit.name };
    const res = await api.get(url, { params: { pageSize: 100 } });
    const item = listItems(res).find((x) => x.id === id);
    return item ? { id: item.id, name: item.name } : { id, name: '' };
  },
});

const typeIs = (formData, type) => Number(formData?.dataScopeType ?? DATA_SCOPE_TYPES.DEFAULT) === type;

/**
 * Kapsam alanları: tür seçimi + türe göre tek hedef listesi (çoklu seçim). `knownTargets` (düzenlemede satırın
 * dataScopeTargets'ı) seçili hedeflerin adlarını ağsız gösterir.
 */
export function buildDataScopeFields(t, knownTargets = []) {
  const options = Object.values(DATA_SCOPE_TYPES).map((id) => ({ id, name: t(`dataScope.types.${TYPE_KEY[id]}`) }));
  return [
    { name: 'dataScopeHint', type: 'notice', content: t('dataScope.hint') },
    {
      name: 'dataScopeType', label: t('dataScope.type'), type: 'select', grid: 12,
      options, idKey: 'id', labelKey: 'name', required: true, defaultValue: DATA_SCOPE_TYPES.DEFAULT,
    },
    {
      name: 'departmentIds', label: t('dataScope.departments'), type: 'asyncSelect', isMulti: true, grid: 12,
      hidden: (f) => !typeIs(f, DATA_SCOPE_TYPES.DEPARTMENTS),
      required: (f) => typeIs(f, DATA_SCOPE_TYPES.DEPARTMENTS),
      ...targetAsync('/v1/departments', knownTargets),
    },
    {
      name: 'locationIds', label: t('dataScope.locations'), type: 'asyncSelect', isMulti: true, grid: 12,
      hidden: (f) => !typeIs(f, DATA_SCOPE_TYPES.LOCATIONS),
      required: (f) => typeIs(f, DATA_SCOPE_TYPES.LOCATIONS),
      ...targetAsync('/v1/locations', knownTargets),
    },
    {
      name: 'subTenantIds', label: t('dataScope.subTenants'), type: 'asyncSelect', isMulti: true, grid: 12,
      hidden: (f) => !typeIs(f, DATA_SCOPE_TYPES.SUB_TENANTS),
      required: (f) => typeIs(f, DATA_SCOPE_TYPES.SUB_TENANTS),
      ...targetAsync('/v1/sub-tenants', knownTargets),
    },
  ];
}

/** Çekmece verisi → istek gövdesi: türe uyan TEK hedef dizisi; diğer listeler yok sayılır. */
export function toDataScopePayload(formData) {
  const dataScopeType = Number(formData?.dataScopeType ?? DATA_SCOPE_TYPES.DEFAULT);
  const field = TARGET_FIELD[dataScopeType];
  if (!field) return { dataScopeType };
  const ids = Array.isArray(formData?.[field]) ? formData[field] : [];
  return { dataScopeType, dataScopeTargetIds: ids };
}

/** Satırın kapsamı → çekmecenin başlangıç değerleri. */
export function dataScopeInitialValues(row) {
  const dataScopeType = Number(row?.dataScopeType ?? DATA_SCOPE_TYPES.DEFAULT);
  const values = { dataScopeType };
  const field = TARGET_FIELD[dataScopeType];
  if (field) values[field] = (row?.dataScopeTargets || []).map((x) => x.id);
  return values;
}

/** "Varsayılan (Modülün Kuralı)" | "Tüm Şirket" | "Seçili Departmanlar (…): A, B" … */
export function formatDataScope(t, row) {
  const type = Number(row?.dataScopeType ?? DATA_SCOPE_TYPES.DEFAULT);
  const label = t(`dataScope.types.${TYPE_KEY[type] || 'default'}`);
  const names = (row?.dataScopeTargets || []).map((x) => x.name).filter(Boolean);
  if (!TARGET_FIELD[type] || names.length === 0) return label;
  return t('dataScope.summary', { type: label, names: names.join(', ') });
}
